import { decompress as zstdDecompress } from 'fzstd';
import { Post, FeedCollection } from '../schema-copy/feed-schema';
import { putIntoQueryCache, getFromQueryCache, isPromise, setMaxCacheSize, putPromiseIntoQueryCache, removeFromQueryCache } from 't-lib-ui/connector';
import { postCountEngine } from './postcount-engine';
import { feedCdnBaseUrl, cdnBaseUrl, FeedCdn } from '../schema-copy/feed-cdn';
import { logWithTime } from '../utils/log';
import { getStoredPost, getStoredPosts } from './offline/post-store';
import { loadPostImages } from './offline/image-swapper';
import { checkThrottled, throwIfThrottled } from './host-throttle';

export const POSTS_PER_FILE = 20;
const CACHE_MAX_AGE_MS = 3600000; // 1 hour
const DRP_HEADER_PREFIX = [68, 82, 80]; // 'DRP' — shared by DRP1 and DRP2
const DRP_INVERT_BYTES = 100;
const DRP1_VERSION_BYTE = 49; // '1' — legacy, no longer supported
const DRP2_VERSION_BYTE = 50; // '2'
const DRP2_ENCRYPTED_BYTE = 101; // 'e' (vs 'x' = 120 for unencrypted)
const GCM_IV_BYTES = 12;

// Imported CryptoKeys cached by base64 passphrase, so each private feed's key is imported once.
const keyCache = new Map<string, Promise<CryptoKey>>();

function importFeedKey(passphrase: string): Promise<CryptoKey> {
  let p = keyCache.get(passphrase);
  if (!p) {
    const raw = Uint8Array.from(atob(passphrase), c => c.charCodeAt(0));
    p = crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['decrypt']);
    keyCache.set(passphrase, p);
  }
  return p;
}

/**
 * Decode a DRP2 response into a FeedCollection.
 * Format: 'DRP2e'/'DRP2x' + payload (first 100 bytes inverted), where the payload is zstd-compressed
 * JSON, AES-256-GCM encrypted (IV(12) ‖ ciphertext ‖ tag(16)) when the version byte is 'e'.
 */
async function decodeDrp2(buffer: ArrayBuffer, passphrase?: string): Promise<FeedCollection> {
  const encrypted = new Uint8Array(buffer, 4, 1)[0] == DRP2_ENCRYPTED_BYTE;
  const payload = new Uint8Array(buffer, 5); // skip 5-byte header ('DRP2' + variant)
  const invertCount = Math.min(DRP_INVERT_BYTES, payload.length);
  for (let i = 0; i < invertCount; i++)
    payload[i] = ~payload[i] & 0xff;

  let compressed: Uint8Array;
  if (encrypted) {
    if (!passphrase) throw new Error('Encrypted feed file but no decryption key available');
    const key = await importFeedKey(passphrase);
    const iv = payload.subarray(0, GCM_IV_BYTES);
    const data = payload.subarray(GCM_IV_BYTES); // ciphertext ‖ tag
    compressed = new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data));
  } else
    compressed = payload;

  return JSON.parse(new TextDecoder().decode(zstdDecompress(compressed)));
}

// Configure cache to hold 300 posts
setMaxCacheSize(300);

// With offline reading, the user whose stored posts (offline/post-store) are consulted before the
// network. Set by the feed engine.
let offlineUserId: string | undefined;

export function setOfflinePostUser(userId: string | undefined): void {
  offlineUserId = userId;
}

// True while the feed engine knows the network is unreachable. With offline reading on, posts then
// come only from the store, and missing ones are replaced by a stub instead of a failing fetch.
let networkOffline = false;

export function setPostNetworkOffline(offline: boolean): void {
  networkOffline = offline;
}

const servesOfflineOnly = () => !!offlineUserId && networkOffline;

/** True while posts come only from the offline store (offline reading on, network unreachable).
 *  Posts rendered meanwhile use the offline version of embeds and media. */
export const isServingOfflineOnly = servesOfflineOnly;

/** Placeholder for a post that wasn't downloaded for offline reading. Never cached, so the real
 *  post loads once the network is back. */
export function offlinePostStub(postIndex: number): Post {
  return {
    number: postIndex,
    title: 'Offline mode: post not downloaded',
    description: '<p>This post is not stored on this device. It will load when you are back online.</p>',
    contentHash: `offline-stub-${postIndex}`,
  };
}

interface FeedInfo {
  id: string;
  postCount?: number; // Optional - will be fetched from postcount-engine if not provided
  /** Base64 AES-256-GCM key for decrypting DRP2e files. Only set for PRIVATE feeds. */
  passphrase?: string;
  /** CDN the server says holds this feed's current.dz — authoritative, overriding the id-prefix
   *  guess. `null` means it isn't uploaded and can only come from the current service; archives
   *  still come from the CDN. Absent means the server didn't say (old feed, never stored), so the
   *  id prefix decides. */
  cdn?: FeedCdn | null;
  /** Sync instance holding this feed's current.dz on local disk (e.g. '01', 'e1'). Absent means
   *  the current service can't be addressed for this feed and only the CDN is available. */
  syncInstance?: string;
}

/** The subset of a feed the post engine needs. Pass the whole feed object where you have one —
 *  cdn/syncInstance are what let it read current.dz from the origin instead of only the CDN. */
export type PostFeedInfo = Pick<FeedInfo, 'id' | 'postCount' | 'passphrase' | 'cdn' | 'syncInstance'>;

/** A feed's CDN folder: `<cdnBase>/<feedId>`. The CDN comes from the feed's stored `cdn` value when
 *  the server sent one; otherwise from the id prefix alone (EU private/newsletter feeds fpe/fpn on
 *  the EU CDN, everything else US) — never from the viewer. */
function feedBaseUrl(feed: FeedInfo): string {
  const base = feed.cdn ? cdnBaseUrl(feed.cdn) : feedCdnBaseUrl(feed.id);
  return `${base.replace(/\/+$/, '')}/${feed.id}`;
}

/** Origin that serves current.dz straight off a sync instance's disk (GET /c/:feedId). */
function currentServiceUrl(feed: FeedInfo, postCount: number, wait: boolean): string {
  return `https://current${feed.syncInstance}.downread.com/c/${feed.id}?p=${postCount}${wait ? '&w' : ''}`;
}

// Feeds added this session. Their current.dz may not have reached the CDN yet — often it doesn't
// even exist on disk at the moment the client first asks — so those reads go straight to the sync
// instance with ?w, which holds the request open until the file appears instead of 404-ing.
const NEW_FEED_WINDOW_MS = 5 * 60 * 1000;
const newFeeds = new Map<string, number>();

/** Mark a just-added feed, so its first current.dz reads go to the instance with ?w. */
export function markFeedAsNew(feedId: string): void {
  newFeeds.set(feedId, Date.now() + NEW_FEED_WINDOW_MS);
}

function isNewFeed(feedId: string): boolean {
  const until = newFeeds.get(feedId);
  if (until == undefined) return false;
  if (Date.now() > until) {
    newFeeds.delete(feedId);
    return false;
  }
  return true;
}

/**
 * Determines the index of the first post in the current file
 */
export function getFirstCurrent(postCount: number): number {
  const mod = postCount % POSTS_PER_FILE;
  return postCount - (mod || POSTS_PER_FILE);
}

/**
 * Determines if a post is in the archive (vs current file)
 */
export function isArchived(postIndex: number, postCount: number): boolean {
  return postIndex < getFirstCurrent(postCount);
}

/**
 * Gets the archive filename for a given post index
 */
export function getArchiveNameForIndex(postIndex: number): string {
  const fileIndex = Math.floor(postIndex / POSTS_PER_FILE) * POSTS_PER_FILE;
  return `p${fileIndex}.dz`;
}

/**
 * Cache key / CDN file name for the current file. The postCount doubles as the cache buster: a
 * feed that gained posts asks for a different URL, so neither the browser nor the CDN edge can
 * hand back the previous, shorter current.dz.
 */
export function getCurrentNameForIndex(postCount: number): string {
  return `current.dz?p=${postCount}`;
}

/**
 * Parse a response as FeedCollection, supporting DRP2 (zstd, optionally encrypted) and plain JSON
 * formats. The passphrase is required to decrypt DRP2e (private feed) files. The legacy DRP1 format
 * is no longer supported and triggers a user-visible error.
 */
export async function parseFeedCollection(buffer: ArrayBuffer, passphrase?: string): Promise<FeedCollection> {
  const header = new Uint8Array(buffer, 0, 4);
  const isDrp = header[0] == DRP_HEADER_PREFIX[0] && header[1] == DRP_HEADER_PREFIX[1] && header[2] == DRP_HEADER_PREFIX[2];
  if (isDrp && header[3] == DRP1_VERSION_BYTE)
    throw new Error('This feed uses an outdated storage format (DRP1) that is no longer supported. Please try again later — it will refresh automatically.');
  if (isDrp && header[3] == DRP2_VERSION_BYTE)
    return decodeDrp2(buffer, passphrase);
  return JSON.parse(new TextDecoder().decode(buffer));
}

/** True when a fetched current.dz actually reaches the post the feed claims to have. A short one
 *  means we read a stale copy (upload still in flight, or a stale CDN edge). */
const reachesPostCount = (collection: FeedCollection, postCount: number): boolean =>
  collection.lastPost >= postCount - 1;

/** Fetch current.dz from the CDN. Returns undefined when it isn't there (404), when the request
 *  fails, or when the copy we got is behind postCount — all cases the origin can still answer. */
async function fetchCurrentFromCdn(feed: FeedInfo, postCount: number, signal?: AbortSignal): Promise<FeedCollection | undefined> {
  const url = `${feedBaseUrl(feed)}/${getCurrentNameForIndex(postCount)}`;
  throwIfThrottled(url);
  const response = await fetch(url, { signal }).catch(e => { if (signal?.aborted) throw e; return undefined; });
  // A 429 throws: falling back to the instance would only add load while we're asked to slow down.
  if (response) checkThrottled(response, url);
  if (!response?.ok) return undefined;
  const collection = await parseFeedCollection(await response.arrayBuffer(), feed.passphrase);
  return reachesPostCount(collection, postCount) ? collection : undefined;
}

/** Fetch current.dz straight from the sync instance that stores it. `wait` adds ?w, which makes
 *  the server hold the request for a few seconds rather than 404 while a sync is in flight. */
async function fetchCurrentFromInstance(feed: FeedInfo, postCount: number, wait: boolean, signal?: AbortSignal): Promise<FeedCollection> {
  const url = currentServiceUrl(feed, postCount, wait);
  throwIfThrottled(url);
  logWithTime(`Current file fetch from instance ${feed.syncInstance}: ${feed.id}${wait ? ' (waiting)' : ''}`);
  const response = await fetch(url, { signal });
  checkThrottled(response, url);
  if (!response.ok)
    throw new Error(`Failed to fetch current.dz for ${feed.id} from instance ${feed.syncInstance}: ${response.status} ${response.statusText}`);
  return parseFeedCollection(await response.arrayBuffer(), feed.passphrase);
}

/**
 * Fetch the feed's current file, choosing between its CDN and the instance that stores it.
 *
 * The CDN is preferred — it is closer and cheaper — but it is a copy that lags: the upload happens
 * after the sync server writes the file, and a feed can have no CDN at all. So the instance is used
 * directly when the feed declares no CDN, when the feed was just added (?w, so the server waits for
 * a file that is about to exist), and as the fallback whenever the CDN answers 404 or hands back a
 * copy that doesn't reach postCount.
 *
 * With no known instance there is nowhere else to go, so that case keeps the old behaviour of
 * retrying the CDN for a while.
 */
async function fetchCurrentCollection(feed: FeedInfo, postCount: number, signal?: AbortSignal): Promise<FeedCollection> {
  const wait = isNewFeed(feed.id);
  const hasInstance = !!feed.syncInstance;
  // Explicit null = server says this feed is on no CDN. Absent = unknown, so the CDN is still worth
  // a try (via the id-prefix guess in feedBaseUrl).
  const noCdn = feed.cdn === null;

  if (hasInstance && (noCdn || wait)) {
    const collection = await fetchCurrentFromInstance(feed, postCount, wait, signal);
    if (reachesPostCount(collection, postCount)) newFeeds.delete(feed.id);
    return collection;
  }

  const fromCdn = await fetchCurrentFromCdn(feed, postCount, signal);
  if (fromCdn) return fromCdn;

  // Missing or stale on the CDN. The origin has it (or is seconds away from having it), so ask it
  // with ?w rather than blindly retrying the CDN.
  if (hasInstance)
    return fetchCurrentFromInstance(feed, postCount, true, signal);

  return fetchCollection(feed, getCurrentNameForIndex(postCount), signal);
}

/** A feed file that isn't on the CDN (404). */
export class FeedFileNotFoundError extends Error {}

/**
 * Fetches a feed collection file from the CDN with retry logic for 404s (a new feed's files may
 * not be uploaded yet). `retryNotFound: false` fails on the first 404 instead.
 */
async function fetchCollection(feed: FeedInfo, fileName: string, signal?: AbortSignal, retryNotFound = true): Promise<FeedCollection> {
  const url = `${feedBaseUrl(feed)}/${fileName}`;
  logWithTime(`Full feed fetch: ${feed.id} (${fileName})`);
  const maxRetries = retryNotFound ? 4 : 1;
  const retryDelayMs = 2500; // 2.5 seconds

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    throwIfThrottled(url);
    const response = await fetch(url + (url.includes('?') ? `&t=${Date.now()}` : attempt ? `?t=${Date.now()}` : ''), { signal }); // prevent caching
    checkThrottled(response, url);

    if (response.ok)
      return parseFeedCollection(await response.arrayBuffer(), feed.passphrase);

    // If 404 and not the last attempt, wait and retry (for new feeds that aren't ready yet)
    if (response.status == 404 && attempt < maxRetries - 1) {
      console.log(`Collection ${fileName} not found (attempt ${attempt + 1}/${maxRetries}), retrying in ${retryDelayMs / 1000}s...`);
      await new Promise(resolve => setTimeout(resolve, retryDelayMs));
      if (signal?.aborted) throw new Error('Aborted');
      continue;
    }

    // For non-404 errors or last attempt, throw immediately
    if (response.status == 404)
      throw new FeedFileNotFoundError(`Collection ${fileName} of ${feed.id} not found`);
    throw new Error(`Failed to fetch collection ${fileName}: ${response.status} ${response.statusText}`);
  }

  // This should never be reached, but TypeScript needs it
  throw new Error(`Failed to fetch collection ${fileName} after ${maxRetries} attempts`);
}

/**
 * Download one file of a feed for offline storage, bypassing the in-memory cache: an archive by
 * name (getArchiveNameForIndex), or the current file when `currentPostCount` is given.
 */
export function downloadFeedFile(feed: PostFeedInfo, fileName: string, signal: AbortSignal, currentPostCount?: number): Promise<FeedCollection> {
  // Archives don't appear later: a 404 is final, so no retries (they only cost requests).
  return currentPostCount == undefined ? fetchCollection(feed, fileName, signal, false) : fetchCurrentCollection(feed, currentPostCount, signal);
}

/** Whether the feed's current file comes straight from its sync instance, without trying the CDN
 *  first (see fetchCurrentCollection). Those can be downloaded in batches (downloadCurrentFiles). */
export function isCurrentFromInstance(feed: PostFeedInfo): boolean {
  return !!feed.syncInstance && feed.cdn === null && !isNewFeed(feed.id);
}

/** The URL downloadFeedFile asks first for this file. Its domain is what the request is throttled
 *  under (host-throttle.ts). */
export function feedFileUrl(feed: PostFeedInfo, fileName: string, currentPostCount?: number): string {
  if (currentPostCount != undefined && feed.syncInstance && (feed.cdn === null || isNewFeed(feed.id)))
    return currentServiceUrl(feed, currentPostCount, false);
  return `${feedBaseUrl(feed)}/${fileName}`;
}

/** Most feeds per downloadCurrentFiles call (the sync server's MAX_BATCH_FEEDS for GET /c). */
export const CURRENT_BATCH_SIZE = 50;

/**
 * Download the current files of up to CURRENT_BATCH_SIZE feeds whose current file comes from the
 * same sync instance (isCurrentFromInstance) in one request: GET /c?ids=…, which answers with each
 * file as a 4-byte big-endian length plus its bytes, in request order. Returns the collections in
 * the order of `feeds`; undefined for a file that is missing, unreadable or behind its postCount —
 * download those one by one (downloadFeedFile), which also builds or waits for them.
 */
export async function downloadCurrentFiles(feeds: { feed: PostFeedInfo, postCount: number }[], signal: AbortSignal): Promise<(FeedCollection | undefined)[]> {
  const url = `https://current${feeds[0].feed.syncInstance}.downread.com/c?ids=${feeds.map(f => f.feed.id).join(',')}`;
  throwIfThrottled(url);
  logWithTime(`Current files fetch from instance ${feeds[0].feed.syncInstance}: ${feeds.length} feeds`);
  const response = await fetch(url, { signal });
  checkThrottled(response, url);
  if (!response.ok)
    throw new Error(`Failed to fetch current files from instance ${feeds[0].feed.syncInstance}: ${response.status} ${response.statusText}`);
  const buffer = await response.arrayBuffer();
  const view = new DataView(buffer);
  const result: (FeedCollection | undefined)[] = [];
  let pos = 0;
  for (const { feed, postCount } of feeds) {
    const length = pos + 4 <= buffer.byteLength ? view.getUint32(pos) : 0;
    pos += 4;
    // A copy: decoding works on the buffer in place.
    const file = buffer.slice(pos, pos += length);
    result.push(length && file.byteLength == length
      ? await parseFeedCollection(file, feed.passphrase).then(c => reachesPostCount(c, postCount) ? c : undefined, () => undefined)
      : undefined);
  }
  return result;
}

/**
 * Loads a collection file and caches all its posts. `currentPostCount` marks the request as being
 * for the current file (rather than an archive) and selects the CDN/instance path for it.
 */
async function loadAndCacheCollection(feed: FeedInfo, fileName: string, currentPostCount?: number): Promise<FeedCollection> {
  const collectionCache = getFromQueryCache<FeedCollection>([feed.id, fileName], CACHE_MAX_AGE_MS);
  let collection: FeedCollection;
  if (collectionCache) {
    if (isPromise(collectionCache))
      collection = await collectionCache;
    else
      collection = collectionCache.data;
  } else {
    const collectionPromise = currentPostCount == undefined
      ? fetchCollection(feed, fileName)
      : fetchCurrentCollection(feed, currentPostCount);
    putPromiseIntoQueryCache<FeedCollection>([feed.id, fileName], collectionPromise, CACHE_MAX_AGE_MS);
    collection = await collectionPromise;

    // Cache all posts from this collection
    for (const post of collection.posts) {
      const key = [feed.id, post.number.toString()];
      putIntoQueryCache(key, post, CACHE_MAX_AGE_MS);
    }

    // Posts are cached, now the collection itself is not needed anymore
    removeFromQueryCache([feed.id, fileName]);
  }

  return collection;
}

/**
 * Gets a single post, using cache or fetching if needed
 */
export async function getSinglePost(
  feed: PostFeedInfo,
  postIndex: number
): Promise<Post> {
  const key = [feed.id, postIndex.toString()];

  // Check cache first
  const cached = getFromQueryCache<Post>(key, CACHE_MAX_AGE_MS);
  if (cached) {
    if (isPromise(cached)) {
      return await cached;
    }
    return cached.data;
  }

  if (offlineUserId) {
    const stored = await getStoredPost(offlineUserId, feed.id, postIndex);
    if (stored) {
      putIntoQueryCache(key, stored, CACHE_MAX_AGE_MS);
      // Offline: have its stored images ready before it renders (see image-swapper).
      if (networkOffline) await loadPostImages(offlineUserId, feed.id, postIndex);
      return stored;
    }
    if (servesOfflineOnly()) return offlinePostStub(postIndex);
  }

  // Get postCount (from feed or postcount-engine)
  const postCount = feed.postCount ?? await postCountEngine.getPostCount(feed.id);

  // Not in cache - need to fetch the collection
  const isInArchive = isArchived(postIndex, postCount);

  if (isInArchive) {
    const collection = await loadAndCacheCollection(feed, getArchiveNameForIndex(postIndex));
    const post = collection.posts.find(p => p.number == postIndex);
    if (!post)
      throw new Error(`Post ${postIndex} not found in archive for ${postIndex}`);
    return post;
  }

  const collection = await loadAndCacheCollection(feed, getCurrentNameForIndex(postCount), postCount);
  if (collection.firstPost > postIndex) {
      const collection = await loadAndCacheCollection(feed, getArchiveNameForIndex(postIndex));
      const post = collection.posts.find(p => p.number == postIndex);
      if (!post)
        throw new Error(`Post ${postIndex} not found in archive for ${postIndex} (after not finding in current.dz)`);
      return post;
  }

  // Find the requested post in the collection
  const post = collection.posts.find(p => p.number == postIndex);
  if (!post)
    throw new Error(`Post ${postIndex} not found in collection current.dz for feed ${feed.id} between ${collection.firstPost} and ${collection.lastPost}`);
  return post;
}

/**
 * Gets multiple posts in a range, using cache or fetching as needed
 * Posts are returned in the requested order (forward or reverse)
 */
export async function getPosts(
  feed: PostFeedInfo,
  postIndexStart: number,
  postIndexEnd: number
): Promise<Post[]> {
  if (postIndexStart == postIndexEnd) return [];
  if (postIndexEnd < postIndexStart) {
    const list = await getPosts(feed, postIndexEnd, postIndexStart);
    return list.reverse();
  }
  
  // Check cache for all posts first
  const cachedPosts = new Map<number, Post>();
  let uncachedIndices: number[] = [];

  for (let postIndex = postIndexStart; postIndex < postIndexEnd; postIndex++) {
    const key = [feed.id, postIndex.toString()];
    const cached = getFromQueryCache<Post>(key, CACHE_MAX_AGE_MS);

    if (cached) {
      if (isPromise(cached))
        cachedPosts.set(postIndex, await cached);
      else 
        cachedPosts.set(postIndex, cached.data);
    } else
      uncachedIndices.push(postIndex);
  }

  // Then the posts stored for offline reading
  if (offlineUserId && uncachedIndices.length) {
    const stored = await getStoredPosts(offlineUserId, feed.id, uncachedIndices);
    if (networkOffline) await Promise.all(Array.from(stored.keys()).map(i => loadPostImages(offlineUserId!, feed.id, i)));
    stored.forEach((post, i) => {
      cachedPosts.set(i, post);
      putIntoQueryCache([feed.id, i.toString()], post, CACHE_MAX_AGE_MS);
    });
    uncachedIndices = uncachedIndices.filter(i => !stored.has(i));
    if (servesOfflineOnly()) {
      uncachedIndices.forEach(i => cachedPosts.set(i, offlinePostStub(i)));
      uncachedIndices = [];
    }
  }

  let fetchStart = postIndexEnd;
  let fetchEnd = postIndexStart;
  for (const postIndex of uncachedIndices) {
    if (postIndex < fetchStart) fetchStart = postIndex;
    if (postIndex > fetchEnd) fetchEnd = postIndex + 1;
  }

  if (uncachedIndices.length == 0) {
    const result: Post[] = [];
    for (let i = postIndexStart; i < postIndexEnd; i++) 
      result.push(cachedPosts.get(i)!);
    return result;
  }

  // Get postCount (from feed or postcount-engine)
  const postCount = feed.postCount ?? await postCountEngine.getPostCount(feed.id);

  // Determine which collection files we need
  const filesToFetch = new Set<string>();
  let cutOff = getFirstCurrent(postCount);
  const needsCurrent = fetchEnd > cutOff;
  for (let i of uncachedIndices)
    if (i >= fetchStart && i < fetchEnd && i < cutOff)
      filesToFetch.add(getArchiveNameForIndex(i));

  // Load all needed collections in parallel
  const loadedFeeds = await Promise.all(
    [
      ...(needsCurrent ? [loadAndCacheCollection(feed, getCurrentNameForIndex(postCount), postCount)] : []),
      ...Array.from(filesToFetch).map(fileName => loadAndCacheCollection(feed, fileName))
    ]
  );
  if (needsCurrent && loadedFeeds[0].firstPost > cutOff) {
    const extraFiles = new Set<string>();
    for (let i = cutOff; i < loadedFeeds[0].firstPost; i+=POSTS_PER_FILE)
      extraFiles.add(getArchiveNameForIndex(i));
    loadedFeeds.push(...await Promise.all(Array.from(extraFiles).map(fileName => loadAndCacheCollection(feed, fileName))));
    cutOff = loadedFeeds[0].firstPost;
  }

  const result = [];
    for (let i = postIndexStart; i < postIndexEnd; i++) {
      const post = cachedPosts.get(i);
      if (post)
        result.push(post);
      else {
        const collection = loadedFeeds.find(c => i >= c.firstPost && i <= c.lastPost);
        if (!collection)
          throw new Error(`Post ${i} not found in any loaded collection for feed ${feed.id}`);
        const post = collection.posts.find(p => p.number == i);
        if (!post)
          throw new Error(`Post ${i} not found in collection ${collection.id} for feed ${feed.id}`);
        result.push(post);
      }
  }

  return result;
}
